import { useIntl } from 'react-intl';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RecordOf } from 'immutable';
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';

// Components
import DepartmentsModal from '../DepartmentsModal.component';
import Ellipsing from 'components/Ellipsing/Ellipsing.component';

// Selectors
import { selectDealershipDepartments } from 'services/DealershipSettings/selectors';

// Types
import { DealershipDepartment } from 'services/resources';

const useDepartmentsTable = () => {
  const intl = useIntl();

  const dealershipDepartments = useSelector(selectDealershipDepartments());
  const [sorting, setSorting] = useState([]);

  const departmentData = useMemo(() => {
    return dealershipDepartments.toArray() as RecordOf<DealershipDepartment>[];
  }, [dealershipDepartments]);

  const columnHelper = createColumnHelper<RecordOf<DealershipDepartment>>();

  const columns = useMemo(() => {
    const columns = [
      columnHelper.accessor((row) => row.get('name'), {
        id: 'name',
        header: intl.formatMessage({
          id: 'views.FleetSettings.Departments.table.name',
        }),
        cell: (cell) => {
          const department = cell.row.original.toJS();
          return <Ellipsing text={department.name} />;
        },
        size: 340,
        enableSorting: true,
      }),
      columnHelper.display({
        id: 'button-actions',
        cell: (cell) => {
          return <DepartmentsModal.DepartmentButton department={cell.row.original.toJSON()} edit />;
        },
        size: 0,
      }),
    ];

    return columns;
  }, [columnHelper, intl]);

  const table = useReactTable<RecordOf<DealershipDepartment>>({
    data: departmentData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    enableSorting: true,
  });

  return { table };
};

export default useDepartmentsTable;
